import React from 'react';

import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined';
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';

type Service = {
  imageSrc: string;
  icon: React.ReactElement;
  title: string;
  summary: string;
  bullets: readonly string[];
};

const SERVICES: readonly Service[] = [
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('Industrial Automation & Control Engineering.jpg')}`,
    icon: <SettingsInputComponentOutlinedIcon fontSize="large" />,
    title: 'Industrial Automation & Control Engineering',
    summary:
      'PLC & SCADA systems that automate operations, improve accuracy, and reduce downtime.',
    bullets: [
      'Custom control design (temperature, pressure, humidity & moisture)',
      'Integration, troubleshooting & repair (cards, PSUs, VSDs)',
    ],
  },
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('Green Energy Solutions (Solar PV).jpg')}`,
    icon: <SolarPowerOutlinedIcon fontSize="large" />,
    title: 'Green Energy Solutions (Solar PV)',
    summary:
      'Engineered solar systems for reliable power—designed for performance, safety, and ROI.',
    bullets: [
      'Residential, commercial/industrial & agricultural solutions',
      'BoS design and commissioning with premium components',
    ],
  },
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('Fabrication, Refractory & Mechanical.jpg')}`,
    icon: <EngineeringOutlinedIcon fontSize="large" />,
    title: 'Fabrication, Refractory & Mechanical',
    summary:
      'Fabrication and mechanical services that keep plants strong, aligned, and reliable.',
    bullets: [
      'Pipe fabrication, steel erection, pumps & motors overhauls',
      'Laser alignment, condition monitoring & shutdown support',
    ],
  },
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('Technical Services, ICT & Security.jpg')}`,
    icon: <BoltOutlinedIcon fontSize="large" />,
    title: 'Technical Services, ICT & Security',
    summary:
      'Security, ICT, and electrical support to protect assets and keep facilities connected.',
    bullets: [
      'CCTV, access control, fire alarms and security integrations',
      'IT networks, electrical distribution, audits & training',
    ],
  },
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('Electrical Installation & Maintenance.jpg')}`,
    icon: <ElectricalServicesOutlinedIcon fontSize="large" />,
    title: 'Electrical Installation & Maintenance',
    summary:
      'Engineered electrical systems that prioritise safety, reliability, and long-term performance across all sectors.',
    bullets: [
      'Residential, commercial/industrial & agricultural electrical systems',
      'Complete design, installation, commissioning & maintenance services',
    ],
  },
  {
    imageSrc: `${process.env.PUBLIC_URL || ''}/images/servicesimages/${encodeURIComponent('maintainence, refrigeration and safety equipment and hardware..jpg')}`,
    icon: <BoltOutlinedIcon fontSize="large" />,
    title: 'Maintenance, Refrigeration & Safety Equipment',
    summary:
      'Structured maintenance solutions, engineered refrigeration systems, and certified safety equipment to ensure operational continuity and compliance.',
    bullets: [
      'Planned preventive maintenance and emergency response services',
      'Cold-chain refrigeration systems and certified safety equipment supply',
    ],
  },
];

export default function HomeServices(): React.ReactElement {
  return (
    <section id="services" className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
            Our Services
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Multi-discipline engineering services designed to reduce downtime, improve
            performance, and deliver long-term reliability.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {SERVICES.map((service) => (
            <div
              key={service.title}
              className="overflow-hidden rounded-2xl bg-white text-left ring-1 ring-slate-200"
            >
              <div className="aspect-[16/9] w-full bg-slate-100">
                <img
                  src={service.imageSrc}
                  alt={service.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    console.error('Failed to load service image:', service.imageSrc);
                  }}
                  onLoad={() => {
                    console.log('Service image loaded:', service.title);
                  }}
                />
              </div>

              <div className="p-8">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-sky-600">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                    {service.title}
                  </h3>
                </div>

                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  {service.summary}
                </p>

                <ul className="mt-5 space-y-2 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {service.bullets.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-sky-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
